import React, { useState, useCallback } from "react";
import { Upload, Image as ImageIcon, X } from "lucide-react";
import { api, resolveAssetUrl } from "../../lib/api";

/* Reusable atomic field components used by EditPageTab */

export const Field = ({ label, children, hint }) => (
  <div style={{ marginBottom: 14 }}>
    <label
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: "#1A1352",
        display: "block",
        marginBottom: 6,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
      }}
    >
      {label}
    </label>
    {children}
    {hint && <div style={{ fontSize: 11, color: "#9A95A8", marginTop: 4 }}>{hint}</div>}
  </div>
);

export const TextInput = ({ value, onChange, ...rest }) => (
  <input
    className="ae-input"
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    {...rest}
  />
);

export const TextArea = ({ value, onChange, rows = 3, ...rest }) => (
  <textarea
    className="ae-input"
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    style={{ resize: "vertical", minHeight: 80 }}
    {...rest}
  />
);

export const Section = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 18,
        border: "1px solid rgba(26,19,82,0.08)",
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          padding: "16px 22px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          fontSize: 16,
          fontWeight: 700,
          color: "#1A1352",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{title}</span>
        <span style={{ color: "#5F5A72", fontSize: 14 }}>{open ? "−" : "+"}</span>
      </button>
      {open && <div style={{ padding: "0 22px 22px" }}>{children}</div>}
    </div>
  );
};

/**
 * Generic upload field. Calls /api/admin/upload (multipart). On success calls onChange(url).
 * - kind: "image" | "video"
 */
export const UploadField = ({ value, onChange, kind = "image", label, accept }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);

  const handleFile = useCallback(
    async (file) => {
      if (!file) return;
      setError("");
      setUploading(true);
      setProgress(0);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", kind);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        });
        if (data?.url) onChange(data.url);
      } catch (e) {
        setError(e?.response?.data?.detail || "Error al subir el archivo");
      } finally {
        setUploading(false);
        setProgress(0);
      }
    },
    [kind, onChange]
  );

  const preview = value ? resolveAssetUrl(value) : null;

  return (
    <div>
      {label && (
        <label
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#1A1352",
            display: "block",
            marginBottom: 6,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {label}
        </label>
      )}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        {preview && kind === "image" && (
          <div style={{ position: "relative" }}>
            <img
              src={preview}
              alt=""
              style={{
                width: 110,
                height: 80,
                objectFit: "cover",
                borderRadius: 10,
                border: "1px solid rgba(26,19,82,0.1)",
              }}
            />
            <button
              type="button"
              onClick={() => onChange("")}
              style={{
                position: "absolute",
                top: -8,
                right: -8,
                background: "#1A1352",
                color: "#fff",
                width: 22,
                height: 22,
                borderRadius: "50%",
                border: "none",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              aria-label="Quitar"
            >
              <X size={12} />
            </button>
          </div>
        )}
        {preview && kind === "video" && (
          <video
            src={preview}
            style={{ width: 200, height: 110, borderRadius: 10, background: "#000", border: "1px solid rgba(26,19,82,0.1)" }}
            muted
          />
        )}
        <div style={{ flex: 1, minWidth: 200 }}>
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 16px",
              borderRadius: 10,
              border: "1.5px dashed rgba(26,19,82,0.25)",
              background: "#FBF9F4",
              color: "#1A1352",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <Upload size={14} />
            {uploading ? `Subiendo… ${progress}%` : kind === "video" ? "Subir vídeo" : "Subir imagen"}
            <input
              type="file"
              accept={accept || (kind === "video" ? "video/*" : "image/*")}
              style={{ display: "none" }}
              disabled={uploading}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </label>
          <TextInput
            value={value || ""}
            onChange={onChange}
            placeholder={kind === "video" ? "...o pega una URL de vídeo" : "...o pega una URL de imagen"}
          />
          {error && <div style={{ fontSize: 12, color: "#E83E0A", marginTop: 4 }}>{error}</div>}
        </div>
      </div>
    </div>
  );
};
